import http from '@/http'
import type { ApiId, ApiResponse } from '@/types'
import { toApiId } from '@/common/utils/id'

export type FeedbackType = 'BUG' | 'SUGGESTION' | 'DATA_ERROR' | 'OTHER'
export type FeedbackStatus = 'PENDING' | 'PROCESSING' | 'WAITING_USER' | 'RESOLVED' | 'CLOSED'
export type FeedbackSenderType = 'USER' | 'STAFF' | 'SYSTEM'

/** 用户端展示用：WAITING_USER 对用户隐藏，显示为处理中；管理端仍用真实状态 */
export function resolveUserFacingStatus(status?: string | null): FeedbackStatus | string {
  if (!status) return ''
  if (status === 'WAITING_USER') return 'PROCESSING'
  return status
}

export interface FeedbackAttachment {
  id: ApiId
  messageId?: ApiId | null
  fileName: string
  fileSize?: number
  fileType?: string
  fileUrl?: string
  status?: string
  createdAt?: string
}

export interface FeedbackMessage {
  id: ApiId
  senderType: FeedbackSenderType
  senderId?: ApiId | null
  senderName?: string
  content: string
  createdAt?: string
  attachments?: FeedbackAttachment[]
}

export interface FeedbackContext {
  pageUrl?: string
  route?: string
  browser?: string
  os?: string
  frontendVersion?: string
  screenWidth?: number
  screenHeight?: number
  language?: string
  timezone?: string
  contextType?: string
  contextId?: string
  submittedAt?: string
}

export interface FeedbackTicketListItem {
  id: ApiId
  ticketNo: string
  type: FeedbackType | string
  title: string
  status: FeedbackStatus | string
  priority?: string
  userId?: ApiId
  userName?: string
  teamId?: ApiId
  assigneeId?: ApiId
  assigneeName?: string
  userUnreadCount?: number
  staffUnreadCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface FeedbackOperationLog {
  id: ApiId
  operatorType?: string
  operatorId?: ApiId
  operatorName?: string
  operation?: string
  oldValue?: string
  newValue?: string
  createdAt?: string
}

export interface FeedbackTicketDetail extends FeedbackTicketListItem {
  description?: string
  closedAt?: string
  /** 用户已读到的最大消息 ID */
  userLastReadMessageId?: ApiId | null
  /** 客服已读到的最大消息 ID */
  staffLastReadMessageId?: ApiId | null
  context?: FeedbackContext | null
  messages?: FeedbackMessage[]
  attachments?: FeedbackAttachment[]
  operationLogs?: FeedbackOperationLog[]
}

export interface CreateFeedbackPayload {
  type: FeedbackType | string
  title: string
  description: string
  attachmentIds?: Array<string | number>
  pageUrl?: string
  route?: string
  browser?: string
  os?: string
  userAgent?: string
  frontendVersion?: string
  screenWidth?: number
  screenHeight?: number
  language?: string
  timezone?: string
  contextType?: string
  contextId?: string
}

export interface CreateFeedbackRes {
  id: ApiId
  ticketNo: string
  status: string
  createdAt?: string
}

export function registerFeedbackAttachment(data: {
  fileUrl: string
  storageKey?: string
  fileName: string
  fileSize?: number
  fileType?: string
}) {
  return http.post<ApiResponse<{
    attachmentId: ApiId
    fileName: string
    fileUrl: string
    status: string
  }>>('/feedback/attachments/register', data)
}

export function createFeedbackTicket(data: CreateFeedbackPayload) {
  return http.post<ApiResponse<CreateFeedbackRes>>('/feedback/tickets', data)
}

export function listMyFeedbackTickets(params?: {
  pageNo?: number
  pageSize?: number
  status?: string
  type?: string
  keyword?: string
}) {
  return http.post<ApiResponse<{
    total: number
    pages: number
    list: FeedbackTicketListItem[]
  }>>('/feedback/tickets/list', {
    pageNo: params?.pageNo || 1,
    pageSize: params?.pageSize || 10,
    status: params?.status,
    type: params?.type,
    keyword: params?.keyword
  })
}

export function getMyFeedbackTicket(id: ApiId) {
  return http.get<ApiResponse<FeedbackTicketDetail>>(`/feedback/tickets/${toApiId(id)}`)
}

export function replyMyFeedbackTicket(id: ApiId, content: string, attachmentIds?: Array<string | number>) {
  return http.post<ApiResponse<void>>(`/feedback/tickets/${toApiId(id)}/messages`, {
    content,
    attachmentIds
  })
}

export function closeMyFeedbackTicket(id: ApiId) {
  return http.post<ApiResponse<void>>(`/feedback/tickets/${toApiId(id)}/close`)
}

export function reopenMyFeedbackTicket(id: ApiId) {
  return http.post<ApiResponse<void>>(`/feedback/tickets/${toApiId(id)}/reopen`)
}

export function listManageFeedbackTickets(params?: {
  pageNo?: number
  pageSize?: number
  status?: string
  type?: string
  keyword?: string
  priority?: string
  userId?: string
  teamId?: number
  assigneeId?: string
}) {
  return http.post<ApiResponse<{
    total: number
    pages: number
    list: FeedbackTicketListItem[]
  }>>('/feedback/manage/tickets/list', {
    pageNo: params?.pageNo || 1,
    pageSize: params?.pageSize || 20,
    status: params?.status,
    type: params?.type,
    keyword: params?.keyword,
    priority: params?.priority,
    userId: params?.userId,
    teamId: params?.teamId,
    assigneeId: params?.assigneeId
  })
}

export function getManageFeedbackTicket(id: ApiId) {
  return http.get<ApiResponse<FeedbackTicketDetail>>(`/feedback/manage/tickets/${toApiId(id)}`)
}

export function replyManageFeedbackTicket(id: ApiId, content: string, attachmentIds?: Array<string | number>) {
  return http.post<ApiResponse<void>>(`/feedback/manage/tickets/${toApiId(id)}/reply`, {
    content,
    attachmentIds
  })
}

export function changeManageFeedbackStatus(id: ApiId, status: string) {
  return http.post<ApiResponse<void>>(`/feedback/manage/tickets/${toApiId(id)}/status`, { status })
}

export function changeManageFeedbackPriority(id: ApiId, priority: string) {
  return http.post<ApiResponse<void>>(`/feedback/manage/tickets/${toApiId(id)}/priority`, { priority })
}

export function assignManageFeedbackTicket(id: ApiId, assigneeId: ApiId) {
  return http.post<ApiResponse<void>>(`/feedback/manage/tickets/${toApiId(id)}/assign`, {
    assigneeId: toApiId(assigneeId)
  })
}
